import { ChevronRight } from 'lucide-react';
import { PANEL_IDS } from '../../config/panelRegistry';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import InfoHint from '../InfoHint';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ErrorText, MutedText, SELECT_CLASS } from './callTypesControls';
import {
  averageScore,
  formatCount,
  formatScore,
  hasOperator,
  scoredCount,
  summarizeQuality
} from './callTypesModel';

const ExtremeCard = ({ title, entry, onSelect, trackId }) => (
  <button
    type="button"
    data-ev={trackId}
    onClick={() => onSelect(entry.employee.employeeId)}
    className="rounded-xl border p-3 text-left hover:border-blue-300 dark:hover:border-blue-800"
  >
    <span className="text-xs text-muted-foreground">
      {title}
      {entry.isLowSample ? ' · мало оценок' : ''}
    </span>
    <strong className="mt-1 block text-sm">{entry.employee.employeeName}</strong>
    <span className="text-sm">
      {formatScore(entry.score)} · оценено {entry.scored}
    </span>
  </button>
);

/** Качество внутри пары «тип → итог». Выбор сотрудника сужает соседний список звонков. */
const EmployeeQualityCard = ({ breakdown, pairCalls, selectedEmployeeId, onSelectEmployee }) => {
  const panels = usePanelSettings();
  const panelId = PANEL_IDS['ui:call-paths.quality'];
  const { employees, forms, formCode, onFormChange, lowSampleThreshold, isLoading, error } = breakdown;
  const withPairCalls = employees.filter((employee) => employee.calls > 0);
  const { totalScored, average, leader, laggard } = summarizeQuality(withPairCalls, formCode, lowSampleThreshold);
  const formLabel = forms.find(({ code }) => code === formCode)?.label;

  return (
    <Card data-panel-id={PANEL_IDS['ui:call-paths.quality']} className="min-w-0">
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-1.5">
          <CardTitle className="text-base">{panels.title(panelId)}</CardTitle>
          <InfoHint text={panels.description(panelId)} label="О качестве сотрудников" trackId="call_paths.quality.hint" />
        </div>
        {forms.length > 1 && (
          <select
            data-ev="call_paths.quality.form"
            aria-label="Форма чек-листа"
            value={formCode ?? ''}
            onChange={(event) => onFormChange(event.target.value)}
            className={SELECT_CLASS}
          >
            {forms.map(({ code, label }) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        )}
      </CardHeader>
      <CardContent>
        {error && <ErrorText>{error}</ErrorText>}
        {!error && isLoading && <MutedText>Загружаем оценки сотрудников…</MutedText>}
        {!error && !isLoading && (
          <>
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 rounded-xl bg-muted/50 p-4">
              <strong className="text-xl tabular-nums">{formatScore(average)}</strong>
              <span className="text-sm text-muted-foreground">
                Оценено {formatCount(totalScored)} из {formatCount(pairCalls)} звонков{formLabel ? ` · ${formLabel}` : ''}
              </span>
            </div>
            {leader && laggard && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <ExtremeCard title="Наибольший средний балл" entry={leader} onSelect={onSelectEmployee} trackId="call_paths.quality.leader" />
                <ExtremeCard title="Наименьший средний балл" entry={laggard} onSelect={onSelectEmployee} trackId="call_paths.quality.laggard" />
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Баллы помогают выбрать звонки для разбора. При малом числе оценок вывод предварительный; частота итога сама по себе не оценивает работу сотрудника.
            </p>
            {withPairCalls.length === 0 && <MutedText>Сотрудников с этим итогом нет.</MutedText>}
            <div className="mt-4 max-h-[360px] divide-y overflow-y-auto">
              {withPairCalls.map((employee) => {
                const content = (
                  <>
                    <span className="min-w-0">
                      <strong className="block">{employee.employeeName}</strong>
                      <span className="text-xs text-muted-foreground">
                        С этим итогом {formatCount(employee.calls)} из {formatCount(employee.denominatorCalls)} звонков типа
                      </span>
                    </span>
                    <span className="ml-auto shrink-0 text-right">
                      <strong className="block tabular-nums">{formatScore(averageScore(employee.scores, formCode))}</strong>
                      <span className="text-xs text-muted-foreground">
                        оценено {scoredCount(employee.scores, formCode)} из {formatCount(employee.calls)}
                      </span>
                    </span>
                  </>
                );

                // Журнал и список звонков не умеют отбирать строку без оператора.
                return hasOperator(employee) ? (
                  <button
                    key={employee.employeeId}
                    type="button"
                    data-ev="call_paths.quality.employee_select"
                    aria-pressed={selectedEmployeeId === employee.employeeId}
                    onClick={() => onSelectEmployee(employee.employeeId)}
                    className={`flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left text-sm hover:bg-muted/50 ${
                      selectedEmployeeId === employee.employeeId ? 'bg-blue-50 dark:bg-blue-950/30' : ''
                    }`}
                  >
                    {content}
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ) : (
                  <div key={employee.employeeId} className="flex items-center gap-3 px-2 py-3 text-sm">
                    {content}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeQualityCard;
